import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useCurrentDomain } from "./context/DomainContext";
import apiClient from './api/client';

const PayerImport = () => {
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [file3, setFile3] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(localStorage.getItem('payerLastUpdated') || '');
  const [autoLoading, setAutoLoading] = useState(false);
  const { domain } = useCurrentDomain();

  const allowedDomainsForPayerImport = [
    'greenyourbills.com',
    'ab.greenyourbills.com'
  ];

  const hasPayerImport = allowedDomainsForPayerImport.includes(domain?.toLowerCase() || '');
  const canImport = hasPayerImport && !!(file1 || file2 || file3) && !loading;
  const resetFiles = () => {
    setFile1(null);
    setFile2(null);
    setFile3(null);

    document.querySelectorAll('input[type="file"]').forEach(input => {
      input.value = '';
    });

    document.querySelectorAll('.upload-box').forEach(box => {
      box.classList.remove('file-uploaded');
    });
  };

  const handleFileChange = (e, setter, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xls', 'xlsx'].includes(ext)) {
      toast.error(`Invalid file type: ${file.name}. Allowed: csv, xls, xlsx`);
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB');
      e.target.value = '';
      return;
    }

    setter(file);
    e.target.parentElement.classList.add('file-uploaded');
  };

  const handleImport = async () => {
    if (!canImport) return;

    setLoading(true);
    const loadingToast = toast.loading('Uploading and processing files...', {
      position: "top-right"
    });

    const formData = new FormData();
    if (file1) formData.append('File1', file1);
    if (file2) formData.append('File2', file2);
    if (file3) formData.append('File3', file3);

    formData.append('UserId', sessionStorage.getItem("userId") || "");
    formData.append('Email', sessionStorage.getItem("userEmail") || "");
    formData.append('DomainFlag', domain);

    try {
      const res = await apiClient.post('/api/PayerImport/UploadFiles', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      toast.dismiss(loadingToast);
      const response = res.data;

      if (response.hasException || response.HasException) {
        toast.error(response.Message || response.message || 'Import failed due to server error', {
          position: "top-right",
          autoClose: 7000,
        });
        return;
      }

      const results = response.Data || response.data || [];

      const successful = results.filter(r => r.isInserted === true || r.IsInserted === true);
      const failed = results.filter(r => r.isInserted === false || r.IsInserted === false);

      const getExpectedColumns = (fileType) => {
        if (!fileType) return [];

        const mappings = {
          "Carisk": [
            "835/EOB", "Payer Name", "Payer Id", "States",
            "Professional", "Institutional", "Pharmacy Rx", "Work Comp", "Automotive"
          ],
          "Availity": [
            "Payer Name", "Payer Short Name", "Payer ID", "Transaction Type (ID)",
            "Portal", "Batch", "Real Time (SOAP)", "REST (API)", "Enrollment Required"
          ],
          "CariskPaper": [
            "Name", "Payer ID", "Address", "Claim Types Accepted", "Jurisdiction"
          ]
        };

        return mappings[fileType] || [];
      };

      if (failed.length === 0 && successful.length > 0) {
        toast.success("Import Completed Successfully", {
          position: "top-right",
          autoClose: 6000,
        });
      } 
      else if (successful.length > 0 && failed.length > 0) {
        let msg = `Partial Import - ${successful.length} succeeded, ${failed.length} failed\n\n`;

        if (successful.length > 0) {
          msg += "✅ Successful:\n";
          successful.forEach(f => {
            msg += `• ${f.fileName || f.FileName} (${f.rowsInserted || f.RowsInserted || 0} rows)\n`;
          });
          msg += "\n";
        }

        msg += "❌ Failed:\n";
        failed.forEach(f => {
          msg += `• ${f.fileName || f.FileName}\n`;
          msg += `  ${f.message || 'Unknown error'}\n`;

          if (f.message?.toLowerCase().includes("columnmapping") || 
              f.message?.toLowerCase().includes("column mapping")) {
            const expected = getExpectedColumns(f.fileType || f.FileType);
            if (expected.length > 0) {
              msg += `  Expected columns for ${f.fileType || f.FileType}:\n`;
              expected.forEach(col => msg += `    - ${col}\n`);
            }
          }
        });

        toast.warning(msg.trim(), {
          position: "top-right",
          autoClose: 15000,
        });
      } 
      else if (failed.length > 0) {
        let msg = `Import Failed\n\n`;

        failed.forEach(f => {
          msg += `• ${f.fileName || f.FileName}\n`;
          msg += `  ${f.message || 'Unknown error'}\n\n`;

          if (f.message?.toLowerCase().includes("columnmapping") || 
              f.message?.toLowerCase().includes("column mapping")) {
            const expected = getExpectedColumns(f.fileType || f.FileType);
            if (expected.length > 0) {
              msg += `Expected columns for ${f.fileType || 'this file type'}:\n`;
              expected.forEach(col => msg += `- ${col}\n`);
            }
          }
        });

        toast.error(msg.trim(), {
          position: "top-right",
          autoClose: 14000,
        });
      } 
      else {
        toast.info("No results returned from server.", { position: "top-right" });
      }

      const now = new Date().toLocaleString();
      localStorage.setItem('payerLastUpdated', now);
      setLastUpdated(now);

      resetFiles();

    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(
        err.response?.data?.Message || 
        err.response?.data?.message || 
        'Upload failed. Please check your connection.', 
        { position: "top-right", autoClose: 7000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAutoImport = async () => {
    if (!hasPayerImport || autoLoading || loading) return;

    setAutoLoading(true);
    const loadingToast = toast.loading('Triggering auto download & import...', {
      position: "top-right"
    });

    try {
      const res = await apiClient.post('/api/PayerImport/TriggerAutoImport', {
        Email: sessionStorage.getItem("userEmail") || "",
        DomainFlag: domain
      });

      toast.dismiss(loadingToast);

      const response = res.data;

      if (response.hasException || response.HasException) {
        toast.error(response.Message || response.message || 'Auto import failed', {
          position: "top-right",
          autoClose: 7000
        });
        return;
      }

      toast.success(response.Message || response.message || 'Auto import triggered successfully', {
        position: "top-right",
        autoClose: 6000
      });

    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(
        err.response?.data?.Message ||
        err.response?.data?.message ||
        'Failed to trigger auto import',
        { position: "top-right", autoClose: 7000 }
      );
    } finally {
      setAutoLoading(false);
    }
  };

  return (
    <div className="container-fluid pt-4" style={{ minHeight: 'calc(100vh - 56px)' }}>
      <div className="card shadow border-0 mx-auto" style={{ maxWidth: '920px' }}>
        <div className="card-header bg-light py-1 d-flex justify-content-between align-items-center">
          <span>
            Domain selected: <strong>{domain || 'Not Selected'}</strong>
          </span>
          {lastUpdated && (
            <small className="text-muted">
              Last updated: <strong>{lastUpdated}</strong>
            </small>
          )}
        </div>
        {!hasPayerImport && domain && (
          <div className="alert alert-danger mx-3 mt-3 mb-0 py-3 text-center fw-medium">
            <i className="fas fa-exclamation-triangle me-2"></i>
            The selected domain doesn't have payer import feature.
          </div>
        )}
        {hasPayerImport ? (
          <div className="card-body p-4">
            <div className="row g-4">
              <div className="col-md-4">
                <div className={`upload-box ${file1 ? 'file-uploaded' : ''}`}>
                  <i className="fas fa-cloud-upload-alt mb-2" style={{ fontSize: '28px', color: '#007bff' }}></i>
                  <p className="mb-1 fw-medium">Carisk Electronic Payer</p>
                  <input
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={(e) => handleFileChange(e, setFile1)}
                    style={{ opacity: 0, position: 'absolute', inset: 0, cursor: 'pointer' }}
                  />
                    <div className="file-name">
                      {!file1 ? (
                        'No file selected'
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <span>{file1.name}</span>

                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              setFile1(null);

                              const uploadBox =
                                e.currentTarget.closest('.upload-box');

                              const input =
                                uploadBox.querySelector('input[type="file"]');

                              if (input) {
                                input.value = '';
                              }

                              uploadBox.classList.remove('file-uploaded');
                            }}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: 'red',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              fontSize: '16px',
                              padding: '0',
                              margin: '0',
                              zIndex: 9999,
                              position: 'relative'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className={`upload-box ${file2 ? 'file-uploaded' : ''}`}>
                  <i className="fas fa-cloud-upload-alt mb-2" style={{ fontSize: '28px', color: '#007bff' }}></i>
                  <p className="mb-1 fw-medium">Availity Payer List</p>
                  <input
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={(e) => handleFileChange(e, setFile2)}
                    style={{ opacity: 0, position: 'absolute', inset: 0, cursor: 'pointer' }}
                  />
                  <div className="file-name">
                    {!file2 ? (
                      'No file selected'
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        <span>{file2.name}</span>

                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            setFile2(null);

                            const uploadBox =
                              e.currentTarget.closest('.upload-box');

                            const input =
                              uploadBox.querySelector('input[type="file"]');

                            if (input) {
                              input.value = '';
                            }

                            uploadBox.classList.remove('file-uploaded');
                          }}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: 'red',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            padding: '0',
                            margin: '0',
                            zIndex: 9999,
                            position: 'relative'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className={`upload-box ${file3 ? 'file-uploaded' : ''}`}>
                  <i className="fas fa-cloud-upload-alt mb-2" style={{ fontSize: '28px', color: '#007bff' }}></i>
                  <p className="mb-1 fw-medium">Carisk Paper Payer List</p>
                  <input
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={(e) => handleFileChange(e, setFile3)}
                    style={{ opacity: 0, position: 'absolute', inset: 0, cursor: 'pointer' }}
                  />
                  <div className="file-name">
                    {!file3 ? (
                      'No file selected'
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        <span>{file3.name}</span>

                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            setFile3(null);

                            const uploadBox =
                              e.currentTarget.closest('.upload-box');

                            const input =
                              uploadBox.querySelector('input[type="file"]');

                            if (input) {
                              input.value = '';
                            }

                            uploadBox.classList.remove('file-uploaded');
                          }}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: 'red',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            padding: '0',
                            margin: '0',
                            zIndex: 9999,
                            position: 'relative'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <button 
              className="btn w-100 mt-4 py-2.5 fw-bold fs-6"
              disabled={!canImport}
              onClick={handleImport}
              style={{
                backgroundColor: canImport ? '#007bff' : '#6c757d',
                color: 'white',
                border: 'none'
              }}
            >
              {loading ? 'Importing... Please wait' : 'Import All Files'}
            </button>
             <button 
              className="btn w-100 mt-4 py-2.5 fw-bold fs-6"
              onClick={handleAutoImport}
              style={{
                backgroundColor: canImport ? '#007bff' : '#3cb377',
                color: 'white',
                border: 'none'
              }}
            >
             {loading ? 'Importing... Please wait' : 'Trigger Auto Download and import files'}
              </button>              
          </div>
        ) : null}
      </div>

      {(loading || autoLoading) && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            border: '6px solid #f3f3f3',
            borderTop: '6px solid #007bff',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      )}

      <style jsx>{`
        .upload-box {
          border: 2px dashed #007bff;
          border-radius: 10px;
          padding: 18px 12px;
          text-align: center;
          cursor: pointer;
          position: relative;
          height: 110px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background-color: #f8f9fa;
          transition: all 0.3s;
        }
        .upload-box:hover { background-color: #e9f2ff; }
        .upload-box.file-uploaded {
          border-color: #3cb377;
          background-color: #e8f5e9;
        }
        .file-name {
          font-size: 12.5px;
          margin-top: 6px;
          word-break: break-all;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PayerImport;